import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTransportDirection } from "@sinch/components/application/Transport/useTransportDirection";
import { RatelistType as RatelistTypeEnum } from "@sinch/core/entities/serverEnums";
import { TransportDirection } from "@sinch/core/entities/serverEnums.types";
import { useFormat } from "@sinch/core/hooks/format";
import { RatelistType } from "@sinch/types/sinch.types";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

/** Single component of a wage composition, as returned by the `wageComposition` field of an attendance */
export interface WageCompositionComponent {
  ratelistType: RatelistType;
  label?: string | null;
  total: number;
}

export interface WageComposition {
  total: number;
  components: WageCompositionComponent[];
}

/** One tooltip line - components of the same ratelist type and label are summed into a single group */
export interface WageCompositionGroup {
  ratelistType: RatelistType;
  label: string | null;
  total: number;
}

/**
 * Tooltip line order, matching the wage breakdown of the job detail page: base wage, tags, transport.
 * Types missing from the list are sorted last, in the order the server returned them.
 */
const WAGE_TYPE_ORDER: Record<string, number> = {
  [RatelistTypeEnum.Wage]: 0,
  [RatelistTypeEnum.TagWage]: 1,
  [RatelistTypeEnum.TransportWage]: 2,
};

/**
 * Merge wage composition components into one group per ratelist type and label, ordered for display.
 * The server splits a group further by rate component name (hourly rate, bonus, ...), which is more
 * detail than the wage cell needs.
 */
export const groupWageComponents = (components: WageCompositionComponent[]): WageCompositionGroup[] => {
  const groups = new Map<string, WageCompositionGroup>();

  components.forEach(({ ratelistType, label, total }) => {
    const key = `${ratelistType}:${label ?? ""}`;
    const group = groups.get(key);
    if (group) {
      group.total += total;
      return;
    }
    groups.set(key, { ratelistType, label: label ?? null, total });
  });

  return Array.from(groups.values()).sort(
    (a, b) => (WAGE_TYPE_ORDER[a.ratelistType] ?? 99) - (WAGE_TYPE_ORDER[b.ratelistType] ?? 99)
  );
};

/**
 * Resolve the display label of a wage group - tags carry the tag name, transport the trip direction
 */
const useGroupLabel = () => {
  const { t } = useTranslation();
  const { getTransportDirectionLabel } = useTransportDirection();

  return ({ ratelistType, label }: WageCompositionGroup): string => {
    switch (ratelistType) {
      case RatelistTypeEnum.Wage:
        return t("CategoryFilter.attendance");
      case RatelistTypeEnum.TagWage:
        return label ?? t("CategoryFilter.tags");
      case RatelistTypeEnum.TransportWage:
        return label === null ? t("CategoryFilter.transport") : getTransportDirectionLabel(label as TransportDirection);
      default:
        return label ?? t("wage");
    }
  };
};

/**
 * Renders the total wage of an attendance with a tooltip breaking it down into base wage, tags and transport.
 * Wages of positions which are not completed yet are only an estimate, marked with a `~` prefix.
 */
export const WageCompositionTooltip: FC<{
  composition: WageComposition;
  approximate: boolean;
}> = ({ composition, approximate }) => {
  const { num } = useFormat();
  const getGroupLabel = useGroupLabel();
  const groups = useMemo(() => groupWageComponents(composition.components), [composition.components]);
  const total = num.precisionCurrency(composition.total);

  return (
    <Tooltip
      title={
        <Stack>
          {groups.map((group) => (
            <Stack key={`${group.ratelistType}:${group.label}`} direction="row" gap={2} justifyContent="space-between">
              <span>{getGroupLabel(group)}</span>
              <span>{num.precisionCurrency(group.total)}</span>
            </Stack>
          ))}
        </Stack>
      }
    >
      <Typography sx={{ whiteSpace: "nowrap" }}>{approximate ? `~${total}` : total}</Typography>
    </Tooltip>
  );
};
